import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined"
import Button from "@mui/material/Button"
import Drawer from "@mui/material/Drawer"
import IconButton from "@mui/material/IconButton"
import Tooltip from "@mui/material/Tooltip"
import Typography from "@mui/material/Typography"
import Grid from "@mui/material/Unstable_Grid2"
import { styled } from "@mui/material/styles"
import { useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { useLocation, useNavigate, useParams } from "react-router-dom"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import CloseableSnackbar from "../../../common/components/CloseableSnackbar"
import Column, { PaddedContentColumn } from "../../../common/components/Column"
import ConditionalPurpleLinearProgress from "../../../common/components/ConditionalLinearProgress"
import PageTopSection from "../../../common/components/PageTopSection"
import Row from "../../../common/components/Row"
import { selectProfileLoaded } from "../../users/reducer"
import { useStartCandidateCall } from "../calling/useStartCandidateCall"
import {
  navigateBackToCandidatesList,
  useCandidateListNavigation,
} from "../candidates/navigation"
import ConfirmConnectEmailDialog, {
  MAIL_PROVIDER_AUTH_STATUS,
  useConnectEmailDialogRequirements,
} from "../components/ConfirmConnectEmailDialog"
import {
  LeadContactActionDialogs,
  useLeadContactActions,
} from "../components/LeadContactActions"
import TaskDialog, { useTaskDialogState } from "../components/TaskDialog"
import {
  SendingEmailTrialFinishedDialogData,
  TrialFinishedRoadblockDialogData,
  useTrialFinishedDialogUtils,
} from "../components/TrialFinishedRoadblockDialog"
import { buildLeadsListPath } from "../my-mls/leadDetailsNavigation"
import {
  mapLeadHistoryToCrmEvents,
  mapLeadToCandidateViewModel,
  mergeCrmEvents,
} from "../my-mls/leadDetailsViewModel"
import { leadDetailsApiPath } from "../my-mls/leadList"
import { useLeadListNavigation } from "../my-mls/useLeadListNavigation"
import { addCRMEventToCandidate, upsertCRMEventToCandidate } from "../utils"
import CandidateDetailsColumn from "./CandidateDetailsColumn"
import ConfirmArchiveCandidateDialog from "./ConfirmArchiveCandidateDialog"
import OffersColumn from "./OffersColumn"
import SendEmailCampaignDialog from "./SendEmailCampaignDialog"
import SendEmailDirectlyDialog from "./SendEmailDirectlyDialog"
import SendEmailMenu, { SEND_REQUEST_TYPE } from "./SendEmailMenu"
import SendSMSCampaignDialog, {
  useManageSendSMSCampaignDialog,
} from "./SendSMSCampaignDialog"
import ActivityTimelineColumn from "./activity-timeline-column"
import { EVENT_TYPES } from "./data"

// Reset shape for navigating between candidates. The component reads
// candidate.id / candidate.is_dnc unguarded, so this must stay an object.
function emptyCandidate() {
  return { crm_stage: {}, crm_events: [], custom_tags: [] }
}

export default function CandidateDetails({ leadMode = null }) {
  const [candidate, setCandidate] = useState(emptyCandidate)
  const [isLoading, setIsLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [offersDrawerOpen, setOffersDrawerOpen] = useState(false)
  const [sendEmailCampaignDialogOpen, setSendEmailCampaignDialogOpen] =
    useState(false)
  const [sendEmailDirectlyDialogOpen, setSendEmailDirectlyDialogOpen] =
    useState(false)
  const [emailMenuRef, setEmailMenuRef] = useState(null)
  const {
    sendSMSCampaignDialogOpen,
    setSendSMSCampaignDialogOpen,
    handleSendSMSButtonClicked,
  } = useManageSendSMSCampaignDialog()
  const {
    confirmConnectEmailDialogOpen,
    openConfirmConnectEmailDialog,
    handleConfirmConnectGmailDialogClosed,
    emailAuthorized,
  } = useConnectEmailDialogRequirements(providerAuthCallback)
  const {
    tasks,
    setTasks,
    clickedTask,
    setClickedTask,
    taskDialogOpen,
    openTaskDialog,
    handleTaskDialogClosed,
    isSnackbarOpen,
    openSnackbar,
    closeSnackbar,
    snackbarMessage,
  } = useTaskDialogState()
  const { requestCall } = useStartCandidateCall(openSnackbar)
  const readOnly = Boolean(leadMode)
  const refreshLeadOutreachRef = useRef(() => {})
  const leadContactActions = useLeadContactActions(openSnackbar, {
    leadSource: leadMode?.source || "mls",
    onOutreachComplete: () => refreshLeadOutreachRef.current(),
  })
  const [rawLead, setRawLead] = useState(null)

  const { id: routeId } = useParams()
  const candidateID = leadMode ? leadMode.id : routeId
  const navigate = useNavigate()
  const location = useLocation()
  const candidateListNav = useCandidateListNavigation()
  const leadListNav = useLeadListNavigation(leadMode?.source, leadMode?.id)
  const {
    canGoPrevious,
    canGoNext,
    hasListContext,
    positionLabel,
    isNavigatingAdjacent,
    goBackToList,
    goToPrevious,
    goToNext,
  } = leadMode ? leadListNav : candidateListNav
  const profileLoaded = useSelector(selectProfileLoaded)
  const axios = useAuthorizedAxiosClient()
  const [confirmRemoveCandidateOpen, setConfirmRemoveCandidateOpen] =
    useState(false)
  const [isRemovingCandidate, setIsRemovingCandidate] = useState(false)
  const { runOnlyIfSubscribed } = useTrialFinishedDialogUtils()

  function providerAuthCallback(status, message) {
    switch (status) {
      case MAIL_PROVIDER_AUTH_STATUS.msIDPopupWindowError:
        openSnackbar(message)
        break
      default:
        break
    }
  }

  function handleEmailMenuClosed(result) {
    if (emailAuthorized) {
      switch (result) {
        case SEND_REQUEST_TYPE.campaign:
          setSendEmailCampaignDialogOpen(true)
          break
        case SEND_REQUEST_TYPE.direct:
          setSendEmailDirectlyDialogOpen(true)
          break
        default:
      }
    } else if (result) {
      openConfirmConnectEmailDialog()
    }
    setEmailMenuRef(null)
  }

  function handleEmailButtonClicked(e) {
    if (leadMode && (rawLead?.is_dnc || candidate.is_dnc)) {
      return
    }
    if (leadMode && rawLead) {
      leadContactActions.handleEmailButtonClicked(e, rawLead)
      return
    }
    runOnlyIfSubscribed(
      () => setEmailMenuRef(e.currentTarget),
      SendingEmailTrialFinishedDialogData
    )
  }

  function handleLeadOrCandidateSmsClicked(e) {
    if (leadMode && (rawLead?.is_dnc || candidate.is_dnc)) {
      return
    }
    if (leadMode && rawLead) {
      leadContactActions.handleSMSButtonClicked(e, rawLead)
      return
    }
    handleSendSMSButtonClicked(e)
  }

  function refreshCandidateCRMEvents() {
    if (leadMode) {
      const params = new URLSearchParams({
        lead_source: leadMode.source,
        lead_ref_id: String(leadMode.id),
      })
      const importedId = rawLead?.candidate_id || candidate.id
      if (importedId) {
        params.set("candidate_id", String(importedId))
      }
      axios
        .get(`/crm/api/leads/calls/history/?${params.toString()}`)
        .then(({ data }) => {
          const mapped = mapLeadHistoryToCrmEvents(
            Array.isArray(data) ? data : []
          )
          setCandidate((prev) => ({
            ...prev,
            crm_events: mergeCrmEvents(prev.crm_events, mapped),
          }))
        })
      return
    }
    axios.get(`/crm/api/candidates/${candidateID}/`).then(({ data }) => {
      setCandidate((prev) => ({ ...prev, crm_events: data.crm_events }))
    })
  }
  refreshLeadOutreachRef.current = refreshCandidateCRMEvents

  function handleSendSMSCampaignDialogClosed(smsSendEvent) {
    if (smsSendEvent) {
      openSnackbar("SMS template sent successully")
      upsertCRMEventToCandidate(setCandidate, smsSendEvent, EVENT_TYPES.sms)
    }
    setSendSMSCampaignDialogOpen(false)
  }

  function handleSendEmailCampaignDialogClosed(emailSendEvent) {
    if (emailSendEvent) {
      openSnackbar("Email template sent successfully")
      addCRMEventToCandidate(setCandidate, emailSendEvent, EVENT_TYPES.email)
    }
    setSendEmailCampaignDialogOpen(false)
  }

  function handleSendEmailDirectlyDialogClosed(emailSendEvent) {
    if (emailSendEvent) {
      openSnackbar("Email sent successfully")
      addCRMEventToCandidate(
        setCandidate,
        emailSendEvent,
        EVENT_TYPES.directEmail
      )
    }
    setSendEmailDirectlyDialogOpen(false)
  }

  function handleBookingRescheduled(payload) {
    if (!payload) {
      return
    }
    setCandidate((prev) => ({
      ...prev,
      stamp: payload.stamp,
      latest_booking_reschedule: payload.timeline_event,
      crm_events: [
        ...(prev.crm_events || []),
        {
          ...payload.timeline_event,
          type: EVENT_TYPES.bookingReschedule,
        },
      ],
    }))
  }

  // A meeting on the user's calendar was rescheduled or cancelled: the
  // backend may have moved the appointment date and added timeline entries.
  function handleCalendarMeetingChanged() {
    axios.get(`/crm/api/candidates/${candidateID}/`).then(({ data }) => {
      setCandidate((prev) => ({
        ...prev,
        stamp: data.stamp,
        crm_events: data.crm_events,
      }))
    })
  }

  function handleCallButtonClicked(e) {
    if (leadMode && (rawLead?.is_dnc || candidate.is_dnc)) {
      return
    }
    if (leadMode && rawLead) {
      leadContactActions.handleCallButtonClicked(e, rawLead)
      return
    }
    requestCall(candidate, {
      onCallEnded: () => refreshCandidateCRMEvents(),
      onAttemptLogged: (updatedCall) => {
        upsertCRMEventToCandidate(setCandidate, updatedCall, EVENT_TYPES.call)
        refreshCandidateCRMEvents()
      },
    })
  }

  function handleAddTaskButtonClicked() {
    runOnlyIfSubscribed(
      () => openTaskDialog(),
      new TrialFinishedRoadblockDialogData(
        "Creating Tasks",
        "Subscribe to create tasks for your candidates"
      )
    )
  }

  function handleCandidateTaskDialogClosed(newOrUpdatedTask, taskAction) {
    handleTaskDialogClosed(newOrUpdatedTask, taskAction)
    if (newOrUpdatedTask) {
      refreshCandidateCRMEvents()
    }
  }

  function handleTaskClicked(task) {
    openTaskDialog()
    setClickedTask(task)
  }

  function handleConfirmRemoveCandidate(confirmed) {
    if (!confirmed) {
      setConfirmRemoveCandidateOpen(false)
      return
    }
    setIsRemovingCandidate(true)
    axios
      .delete(`/crm/api/candidates/${candidateID}/`)
      .then(() => navigateBackToCandidatesList(navigate, location))
      .catch(() => openSnackbar("Could not remove candidate. Try again."))
      .finally(() => {
        setIsRemovingCandidate(false)
        setConfirmRemoveCandidateOpen(false)
      })
  }

  useEffect(
    () => {
      if (!profileLoaded) {
        return
      }
      if (leadMode) {
        let cancelled = false
        setIsLoading(true)
        setNotFound(false)
        axios
          .get(leadDetailsApiPath(leadMode.source, leadMode.id))
          .then(async ({ data: lead }) => {
            if (cancelled) return
            setRawLead(lead)
            const historyParams = new URLSearchParams({
              lead_source: leadMode.source,
              lead_ref_id: String(lead.id),
            })
            if (lead.candidate_id) {
              historyParams.set("candidate_id", String(lead.candidate_id))
            }
            const historyRequest = axios.get(
              `/crm/api/leads/calls/history/?${historyParams.toString()}`
            )
            if (lead.candidate_id) {
              const [{ data: candidateDetails }, { data: history }] =
                await Promise.all([
                  axios.get(`/crm/api/candidates/${lead.candidate_id}/`),
                  historyRequest,
                ])
              if (cancelled) return
              const mapped = mapLeadHistoryToCrmEvents(
                Array.isArray(history) ? history : []
              )
              setCandidate(
                mapLeadToCandidateViewModel(lead, leadMode.source, {
                  ...candidateDetails,
                  crm_events: mergeCrmEvents(
                    candidateDetails.crm_events,
                    mapped
                  ),
                })
              )
              return
            }
            const { data: history } = await historyRequest
            if (cancelled) return
            const view = mapLeadToCandidateViewModel(
              lead,
              leadMode.source,
              null
            )
            view.crm_events = mapLeadHistoryToCrmEvents(
              Array.isArray(history) ? history : []
            )
            setCandidate(view)
          })
          .catch((error) => {
            if (!cancelled && error?.response?.status === 404) {
              setNotFound(true)
            }
          })
          .finally(() => {
            if (!cancelled) setIsLoading(false)
          })
        return () => {
          cancelled = true
        }
      }
      setIsLoading(true)
      setNotFound(false)
      // Clear the previous candidate before loading the next one. The route is
      // registered without a key, so prev/next navigation reuses the same
      // component instance: the page kept rendering the previous person - name,
      // phone, stage, timeline - with Call and Send email live against them for
      // the whole duration of the new request.
      setCandidate(emptyCandidate)
      let cancelled = false
      axios
        .get(`/crm/api/candidates/${candidateID}/`)
        .then(({ data }) => {
          if (cancelled) {
            return
          }
          setCandidate(data)
        })
        .catch((error) => {
          if (cancelled) {
            return
          }
          if (error?.response?.status === 404) setNotFound(true)
        })
        .finally(() => {
          if (!cancelled) {
            setIsLoading(false)
          }
        })

      const onVisibility = () => {
        if (document.visibilityState === "visible") {
          refreshCandidateCRMEvents()
        }
      }
      document.addEventListener("visibilitychange", onVisibility)
      return () => {
        cancelled = true
        document.removeEventListener("visibilitychange", onVisibility)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [candidateID, profileLoaded, leadMode?.source, leadMode?.id]
  )

  useEffect(
    () => {
      if (!profileLoaded) {
        return
      }
      const taskCandidateId = leadMode ? rawLead?.candidate_id : candidateID
      if (!taskCandidateId) {
        if (leadMode) setTasks([])
        return
      }
      axios
        .get(`/crm/api/tasks/candidate/${taskCandidateId}/`)
        .then(({ data }) => setTasks(data))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [profileLoaded, candidateID, leadMode?.id, rawLead?.candidate_id]
  )

  return (
    <Column>
      <PageTopSection
        title={
          leadMode
            ? leadMode.source === "uploaded"
              ? "Uploaded lead details"
              : "MLS agent details"
            : "Candidate Details"
        }
      >
        <Row sx={{ alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={
              leadMode
                ? () => navigate(buildLeadsListPath(leadMode.source))
                : goBackToList
            }
            sx={{ fontWeight: 500 }}
          >
            {leadMode ? "Back to leads" : "Back to candidates"}
          </Button>
          {leadMode && (rawLead?.candidate_id || candidate.id) ? (
            <Button
              variant="outlined"
              color="primary"
              onClick={() =>
                navigate(`/candidates/${rawLead?.candidate_id || candidate.id}`)
              }
              sx={{ fontWeight: 500 }}
            >
              Open in Candidates
            </Button>
          ) : null}
          {hasListContext ? (
            <>
              {positionLabel ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mx: { xs: 0, sm: 1 } }}
                >
                  {positionLabel}
                </Typography>
              ) : null}
              <Button
                variant="outlined"
                color="primary"
                startIcon={<ChevronLeftIcon />}
                disabled={!canGoPrevious || isNavigatingAdjacent || isLoading}
                onClick={goToPrevious}
                sx={{ fontWeight: 500 }}
              >
                {leadMode ? "Previous lead" : "Previous candidate"}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                endIcon={<ChevronRightIcon />}
                disabled={!canGoNext || isNavigatingAdjacent || isLoading}
                onClick={goToNext}
                sx={{ fontWeight: 500 }}
              >
                {leadMode ? "Next lead" : "Next candidate"}
              </Button>
            </>
          ) : null}
          {readOnly ? null : (
            <Tooltip title="Remove from portal">
              <span>
                <IconButton
                  color="inherit"
                  aria-label="Remove candidate from portal"
                  disabled={
                    isLoading || isRemovingCandidate || candidateID == null
                  }
                  onClick={() => setConfirmRemoveCandidateOpen(true)}
                >
                  <DeleteOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Row>
      </PageTopSection>
      <ConditionalPurpleLinearProgress visible={isLoading} />
      <PaddedContentColumn>
        {notFound ? (
          <Column sx={{ alignItems: "center", gap: 2, py: 8 }}>
            <Typography variant="h6">No data found</Typography>
            <Typography variant="body2" color="text.secondary">
              {leadMode
                ? "This lead doesn't exist or is no longer available."
                : "This candidate doesn't exist or is no longer available."}
            </Typography>
            <Button
              variant="contained"
              onClick={() =>
                leadMode
                  ? navigate(buildLeadsListPath(leadMode.source))
                  : navigate("/candidates")
              }
            >
              {leadMode ? "Go to leads" : "Go to candidates"}
            </Button>
          </Column>
        ) : (
          <Grid container spacing={2}>
            <Grid xs={12} sm={3.5}>
              <CandidateDetailsColumn
                candidate={candidate}
                setCandidate={setCandidate}
                isLoading={isLoading}
                readOnly={readOnly}
                leadMode={leadMode}
                onEmailButtonClicked={handleEmailButtonClicked}
                onSendSMSButtonClicked={handleLeadOrCandidateSmsClicked}
                onCallButtonClicked={handleCallButtonClicked}
                onTaskButtonClicked={handleAddTaskButtonClicked}
              />
            </Grid>
            <Grid xs={12} sm={8.5}>
              <ActivityTimelineColumn
                candidate={candidate}
                setCandidate={setCandidate}
                isLoading={isLoading}
                readOnly={readOnly}
                onEmailButtonClicked={handleEmailButtonClicked}
                onSendSMSButtonClicked={handleLeadOrCandidateSmsClicked}
                onMakeCallButtonClicked={handleCallButtonClicked}
                tasks={tasks}
                setTasks={setTasks}
                onAddTaskButtonClicked={handleAddTaskButtonClicked}
                onTaskClicked={handleTaskClicked}
                onCRMEventsChanged={refreshCandidateCRMEvents}
                onBookingRescheduled={handleBookingRescheduled}
                onMeetingChanged={handleCalendarMeetingChanged}
              />
            </Grid>
          </Grid>
        )}
      </PaddedContentColumn>
      {readOnly ? null : (
        <>
          <OffersDrawerButton
            variant="contained"
            offersDrawerOpen={offersDrawerOpen}
            onClick={() => setOffersDrawerOpen(!offersDrawerOpen)}
          >
            <ChevronLeftIcon
              sx={{
                transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1) 0ms",
                transform: offersDrawerOpen ? "rotate(0deg)" : "rotate(180deg)",
              }}
            />
            Proposals
          </OffersDrawerButton>
          <Drawer
            anchor="right"
            open={offersDrawerOpen}
            onClose={() => setOffersDrawerOpen(false)}
            transitionDuration={300}
            sx={{
              "& .MuiDrawer-paper": {
                width: { xs: "calc(100% - 50px)", sm: 400 },
              },
            }}
          >
            <OffersColumn
              candidate={candidate}
              setCandidate={setCandidate}
              isLoading={isLoading}
            />
          </Drawer>
        </>
      )}
      <SendEmailCampaignDialog
        open={sendEmailCampaignDialogOpen}
        onClose={handleSendEmailCampaignDialogClosed}
        candidate={candidate}
      />
      <SendEmailDirectlyDialog
        open={sendEmailDirectlyDialogOpen}
        onClose={handleSendEmailDirectlyDialogClosed}
        candidate={candidate}
      />
      <SendSMSCampaignDialog
        open={sendSMSCampaignDialogOpen}
        onClose={handleSendSMSCampaignDialogClosed}
        candidate={candidate}
      />
      <ConfirmConnectEmailDialog
        open={confirmConnectEmailDialogOpen}
        onClose={handleConfirmConnectGmailDialogClosed}
      />
      <SendEmailMenu
        anchorElem={emailMenuRef}
        onClose={handleEmailMenuClosed}
      />
      <TaskDialog
        open={taskDialogOpen}
        onClose={handleCandidateTaskDialogClosed}
        task={clickedTask}
        candidate={candidate}
      />
      <ConfirmArchiveCandidateDialog
        open={confirmRemoveCandidateOpen}
        candidateName={candidate.candidate_name}
        onClose={handleConfirmRemoveCandidate}
      />
      {leadMode ? (
        <LeadContactActionDialogs actionsState={leadContactActions} />
      ) : null}
      <CloseableSnackbar
        open={isSnackbarOpen}
        onClose={closeSnackbar}
        message={snackbarMessage}
      />
    </Column>
  )
}

const OffersDrawerButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "offersDrawerOpen",
})(({ theme, offersDrawerOpen }) => ({
  position: "fixed",
  [theme.breakpoints.only("xs")]: {
    right: offersDrawerOpen ? "calc(100vw - 65px)" : 0,
    top: "200px",
  },
  right: offersDrawerOpen ? "363px" : "-32px",
  top: "40%",
  transform: "translateY(-50%)",
  zIndex: 1201,
  transition: "right 300ms cubic-bezier(0, 0, 0.2, 1) 0ms",
  backgroundColor: (theme) => theme.palette.colors.primary,
  "&:hover": {
    backgroundColor: "#4A1B65",
  },
  writingMode: "vertical-rl",
  textOrientation: "mixed",
  padding: "12px 8px",
  minWidth: "auto",
  borderRadius: "0 8px 8px 0",
  rotate: "180deg",
  textTransform: "uppercase",
  gap: theme.spacing(1),
  boxShadow: "none",
}))
